import * as React from 'react';

import type { SalesforceLead } from 'bundles/enterprise-admin-contracts/utils/SalesforceAPI';

type Org = { label: string; value: string; slug?: string };

export type Invoice = {
  owner: string;
  existingOrg?: Org;
  company: string;
  organizationType: string;
  customerEmail: string;
  leadId: string;
  seats: number;
  billingType: string;

  // attributes from geo price
  priceType: string;
  unitPrice: string;
  currencyCode: string;
  amountTotal: string;

  firstName: string;
  lastName: string;
  role: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  country?: { label: string; value: string };
  taxId: string;
  taxIdTypeCode?: string;
  poNumber?: string;
};

type EnterpriseLeadContextState = {
  lead?: SalesforceLead;
  setLead: (value: SalesforceLead) => void;

  purchaseUserId: number | null;
  setPurchaseUserId: (userId: number | null) => void;

  invoice: Invoice;
  /**
   * Partially update Invoice. Provided value will be merged to current value
   */
  updateInvoice: (value: Partial<Invoice>) => void;

  resetInvoice: () => void;
};

const emptyInvoice: Invoice = {
  owner: 'COURSERA_SALES',
  existingOrg: undefined,
  company: '',
  organizationType: '',
  customerEmail: '',
  leadId: '',
  seats: 5,
  billingType: 'annual',

  priceType: 'standard',
  unitPrice: '',
  amountTotal: '',
  currencyCode: '',

  firstName: '',
  lastName: '',
  role: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  postalCode: '',
  country: undefined,
  taxId: '',
  taxIdTypeCode: '',
  poNumber: '',
};

export const EnterpriseLeadContext = React.createContext<EnterpriseLeadContextState>({
  invoice: emptyInvoice,
  lead: undefined,
  purchaseUserId: null,
  updateInvoice: () => null,
  resetInvoice: () => null,
  setLead: () => null,
  setPurchaseUserId: () => null,
});

const EnterpriseLeadContainer: React.LegacyFunctionComponentWithChildren = ({ children }) => {
  const [invoice, setInvoice] = React.useState<Invoice>(emptyInvoice);
  const [lead, setLead] = React.useState<SalesforceLead>();
  const [purchaseUserId, setPurchaseUserId] = React.useState<number | null>(null);

  const updateInvoice = (value: Partial<Invoice>) => setInvoice((prevState) => ({ ...prevState, ...value }));
  const resetInvoice = () => setInvoice(emptyInvoice);

  return (
    <EnterpriseLeadContext.Provider
      value={{
        invoice,
        lead,
        purchaseUserId,
        updateInvoice,
        setLead,
        setPurchaseUserId,
        resetInvoice,
      }}
    >
      {children}
    </EnterpriseLeadContext.Provider>
  );
};

export default EnterpriseLeadContainer;
